import type {
  ParameterCategory,
  ParameterSource,
  StatusMapping,
  MachineStatusValue,
} from "@opus-dt/shared-types";

export type { ParameterCategory, StatusMapping, MachineStatusValue };

export const CATEGORY_SOURCES: Record<ParameterCategory, ParameterSource[]> = {
  STATUS: ["SENSOR", "INTEGRATION", "MANUAL"],
  NUMERIC: ["MANUAL", "SENSOR", "IMPORT", "INTEGRATION", "CALCULATED"],
  COUNTER: ["SENSOR", "MANUAL", "IMPORT", "CALCULATED"],
  BOOLEAN: ["SENSOR", "MANUAL", "INTEGRATION"],
  TEXT: ["MANUAL", "IMPORT", "INTEGRATION"],
  DURATION: ["SENSOR", "MANUAL", "IMPORT", "CALCULATED"],
};

export const CATEGORY_DATA_TYPE: Record<ParameterCategory, string> = {
  STATUS: "enum",
  NUMERIC: "number",
  COUNTER: "integer",
  BOOLEAN: "boolean",
  TEXT: "text",
  DURATION: "duration",
};

export interface CategoryConfig {
  category: ParameterCategory;
  label: string;
  description: string;
}

export const CATEGORY_CONFIGS: readonly CategoryConfig[] = [
  {
    category: "STATUS",
    label: "Status",
    description:
      "Governa o estado da máquina (rodando, parada, ociosa). Único por máquina. Sem unidade.",
  },
  {
    category: "NUMERIC",
    label: "Medida numérica",
    description: "Grandeza física medida (temperatura, pressão, peso, etc).",
  },
  {
    category: "COUNTER",
    label: "Contagem",
    description: "Quantidades produzidas, ciclos, batidas.",
  },
  {
    category: "BOOLEAN",
    label: "Booleano",
    description: "Estado binário (ligado/desligado, presente/ausente). Sem unidade.",
  },
  {
    category: "TEXT",
    label: "Texto",
    description: "Identificador, lote, observação. Sem unidade.",
  },
  {
    category: "DURATION",
    label: "Duração",
    description: "Tempo de ciclo ou processo. Unidade fixa (s/min/h).",
  },
] as const;

export function getSourceDescription(category: ParameterCategory, source: ParameterSource): string {
  if (category === "STATUS") {
    if (source === "SENSOR" || source === "INTEGRATION") {
      return "Muda status automaticamente a cada leitura";
    }
    if (source === "MANUAL") {
      return "Status atualizado pelo operador na aba Operação";
    }
  }
  const generic: Record<ParameterSource, string> = {
    MANUAL: "Preenchido manualmente pelo operador",
    SENSOR: "Atualizado por leituras de sensor",
    INTEGRATION: "Atualizado por integração externa",
    IMPORT: "Carregado via importação de dados",
    CALCULATED: "Calculado a partir de outros parâmetros",
  };
  return generic[source];
}

export interface StatusMappingEntry {
  value: string;
  status: MachineStatusValue;
}

export function mappingArrayToObject(entries: StatusMappingEntry[]): StatusMapping {
  const out: StatusMapping = {};
  for (const e of entries) {
    if (e.value.trim()) out[e.value.trim()] = e.status;
  }
  return out;
}

export function mappingObjectToArray(
  mapping: StatusMapping | null | undefined
): StatusMappingEntry[] {
  if (!mapping) return [];
  return Object.entries(mapping).map(([value, status]) => ({ value, status }));
}

// Spec 081 — o vocabulário do contador em pt-BR. O identificador do código continua em inglês
// (é o valor do enum que atravessa o contrato); o que aparece em tela nunca.
export type ProductionRole = "PRODUCES" | "CONSUMES" | "SCRAPS";
export type LossTypeValue = "REJECT" | "WASTE" | "REWORK";

export const PRODUCTION_ROLE_LABELS: Record<ProductionRole, string> = {
  PRODUCES: "Produz",
  CONSUMES: "Consome",
  SCRAPS: "Refuga",
};

/** O que cada sentido faz com o número, dito para quem configura, não para quem programa. */
export const PRODUCTION_ROLE_HINTS: Record<ProductionRole, string> = {
  PRODUCES: "Soma ao produzido da execução.",
  CONSUMES: "Registra a matéria-prima que entra; não soma ao produzido nem ao refugo.",
  SCRAPS: "Registra perda do tipo declarado abaixo.",
};

// O MESMO vocabulário das telas que já existem (`ScrapEntryBlock`, `LossesCard`, narração do
// assistente): REJECT é "Refugo". Sinônimo novo faz a mesma linha de perda aparecer com dois nomes
// em painéis vizinhos.
export const LOSS_TYPE_LABELS: Record<LossTypeValue, string> = {
  REJECT: "Refugo",
  WASTE: "Desperdício",
  REWORK: "Retrabalho",
};
